import React, { useState } from 'react';
import {
  X,
  Plus,
  Target,
  FileText,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  Sparkles,
  Zap,
  SlidersHorizontal
} from 'lucide-react';
import { productOSStore, ProductBet } from '../../services/product/productOSStore';

interface ProductBetPRDStudioProps {
  bet?: ProductBet | null;
  onClose: () => void;
}

export const ProductBetPRDStudio: React.FC<ProductBetPRDStudioProps> = ({ bet, onClose }) => {
  const [title, setTitle] = useState(bet?.title || '');
  const [hypothesis, setHypothesis] = useState(
    bet?.hypothesis || 'Si nous offrons X pour le segment Y, nous obtiendrons l\'outcome Z car evidence W.'
  );
  const [targetOutcome, setTargetOutcome] = useState(bet?.targetOutcome || '');
  const [killCriteria, setKillCriteria] = useState(bet?.killCriteria || '');
  const [timeboxWeeks, setTimeboxWeeks] = useState(bet?.timeboxWeeks || 4);
  const [investmentTier, setInvestmentTier] = useState<'SMALL' | 'MEDIUM' | 'LARGE'>(bet?.investmentTier || 'MEDIUM');
  const [goals, setGoals] = useState<string>('Exécuter le calcul en < 300ms, afficher la répartition transparente');
  const [nonGoals, setNonGoals] = useState<string>('Pas de monétisation forcée, pas d\'inscription obligatoire');

  const [createdNotice, setCreatedNotice] = useState<boolean>(false);

  const handleSaveBetAndPRD = () => {
    if (!title) return;

    const createdBet = productOSStore.addBet({
      opportunityId: 'opp-gen-' + Date.now(),
      title,
      hypothesis,
      targetOutcome,
      timeboxWeeks,
      investmentTier,
      owner: 'Product Manager',
      successCriteria: targetOutcome,
      killCriteria,
      status: 'APPROVED',
      confidencePct: 85
    });

    productOSStore.addPRD({
      betId: createdBet.id,
      title: `PRD — ${title}`,
      version: 'v1.0',
      owner: 'Product Manager',
      problemStatement: hypothesis,
      goals: goals.split(',').map((g) => g.trim()),
      nonGoals: nonGoals.split(',').map((ng) => ng.trim()),
      userJourneys: ['Parcours utilisateur standard'],
      functionalRequirements: ['Exécution déterministe certifiée'],
      nonFunctionalRequirements: ['Latence P95 < 300ms', 'SLA 99.99%'],
      analyticsEventsRequired: ['feature_bet_executed', 'feature_bet_completed'],
      securityPrivacyReviewPassed: true,
      status: 'APPROVED'
    });

    setCreatedNotice(true);
    setTimeout(() => {
      onClose();
    }, 1000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full border border-slate-200 shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center">
              <Target className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">
                {bet ? 'Édition Pari Produit & PRD' : 'Nouveau Pari Produit & PRD (Outcome Driven)'}
              </h2>
              <p className="text-xs text-slate-500 font-mono">YamooPay Product Operating System Gate</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-200 rounded-lg text-slate-500 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5 text-sm">
          {createdNotice && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 font-bold text-xs flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              Pari Produit & PRD créés avec succès et intégrés à la Roadmap !
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1 uppercase">Titre du Pari Produit</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="ex: Pari — Simulateur Transfrontalier Reste à Vivre"
              className="w-full p-2.5 rounded-lg border border-slate-300 bg-slate-50 focus:bg-white text-xs font-bold"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1 uppercase">
              Hypothèse Falsifiable (&quot;If we do X...&quot;)
            </label>
            <textarea
              rows={3}
              value={hypothesis}
              onChange={(e) => setHypothesis(e.target.value)}
              className="w-full p-2.5 rounded-lg border border-slate-300 bg-slate-50 focus:bg-white text-xs font-mono"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-emerald-800 mb-1 uppercase">Résultat Visé (Target Outcome)</label>
              <textarea
                rows={2}
                value={targetOutcome}
                onChange={(e) => setTargetOutcome(e.target.value)}
                placeholder="ex: Augmentation de +15% de la rétention D30"
                className="w-full p-2.5 rounded-lg border border-emerald-300 bg-emerald-50/40 focus:bg-white text-xs text-emerald-900"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-rose-800 mb-1 uppercase">Critères d&apos;Arrêt (Kill Criteria)</label>
              <textarea
                rows={2}
                value={killCriteria}
                onChange={(e) => setKillCriteria(e.target.value)}
                placeholder="ex: Si adoption D14 < 5% ou latence > 2.5s"
                className="w-full p-2.5 rounded-lg border border-rose-300 bg-rose-50/40 focus:bg-white text-xs text-rose-900"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1 uppercase">Timebox (Semaines)</label>
              <input
                type="number"
                value={timeboxWeeks}
                onChange={(e) => setTimeboxWeeks(parseInt(e.target.value) || 4)}
                className="w-full p-2 rounded border border-slate-300 text-xs"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1 uppercase">Investissement</label>
              <select
                value={investmentTier}
                onChange={(e) => setInvestmentTier(e.target.value as any)}
                className="w-full p-2 rounded border border-slate-300 text-xs font-bold"
              >
                <option value="SMALL">SMALL (1-2 Sprints)</option>
                <option value="MEDIUM">MEDIUM (3-4 Sprints)</option>
                <option value="LARGE">LARGE (Quarterly Epic)</option>
              </select>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4 space-y-3">
            <h3 className="text-xs font-bold uppercase text-blue-700">PRD Spécifications & Non-Goals</h3>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Objectifs (séparés par virgule)</label>
              <input
                type="text"
                value={goals}
                onChange={(e) => setGoals(e.target.value)}
                className="w-full p-2 rounded border border-slate-300 text-xs"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-rose-700 mb-1">Non-Goals Explicites</label>
              <input
                type="text"
                value={nonGoals}
                onChange={(e) => setNonGoals(e.target.value)}
                className="w-full p-2 rounded border border-rose-300 bg-rose-50/20 text-xs text-rose-900 font-bold"
              />
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 text-xs font-bold hover:bg-slate-100 transition-colors cursor-pointer"
          >
            Annuler
          </button>
          <button
            onClick={handleSaveBetAndPRD}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition-colors cursor-pointer"
          >
            Valider Pari Produit & PRD
          </button>
        </div>
      </div>
    </div>
  );
};
